<template>
  <BaseModule
    v-bind="$props"
    class="highlight-title"
    data-module="highlight-title"
    :style="{
      backgroundColor,
      backgroundImage: bgImage,
    }"
    :class="{
      'highlight-title--dark':
        filterType === 'dark' && backgroundType !== 'color',
      'highlight-title--light':
        filterType === 'light' && backgroundType !== 'color',
    }"
  >
    <div
      class="highlight-title__container"
      :style="{
        '--text-position': textPosition,
      }"
    >
      <div class="highlight-title__content">
        <header
          v-if="title"
          class="base-title-fontsize"
        >
          <component
            :is="titleTag"
            v-if="title"
            class="tit"
          >
            <div v-html="title"></div>
          </component>
        </header>
      </div>
      <div class="highlight-title__content">
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
        <div
          v-if="buttons.length"
          class="highlight-title__buttons"
        >
          <ul>
            <li
              v-for="(button, index) in buttons"
              :key="index"
            >
              <EditorLink
                v-if="button.buttonText"
                :model="button.buttonUrl"
                class="cta button"
                :class="{ 'button--featured': button.buttonFeatured }"
              >
                {{ button.buttonText }}
              </EditorLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import base from '@/mixins/base';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';

export default {
  name: 'HighlightTitle',
  components: {
    BaseModule,
    EditorLink,
  },
  mixins: [base],
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    textPosition: {
      type: String,
      default: 'row',
    },
    buttons: {
      type: Array,
      default() {
        return [];
      },
    },
    backgroundType: {
      type: String,
      default: 'color',
    },
    backgroundImage: {
      type: String,
      default: '',
    },
    differentMobileBackgroundImage: {
      type: Boolean,
      default: false,
    },
    backgroundImageMobile: {
      type: String,
      default: '',
    },
    filterType: {
      type: String,
      default: 'dark',
    },
  },
  data() {
    return {
      isMobile: window.innerWidth < 640,
    };
  },
  computed: {
    bgImage() {
      const image =
        this.differentMobileBackgroundImage && this.isMobile
          ? this.backgroundImageMobile
          : this.backgroundImage;
      return this.backgroundType === 'image' && image ? `url('${image}')` : '';
    },
  },
  mounted() {
    this.isMobile = window.innerWidth < 640;

    window.addEventListener('resize', () => {
      const isMobile = window.innerWidth < 640;

      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    });
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'title',
                type: 'Html',
                label: 'Título',
                wrapWithParagraph: false,
              },
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
              {
                name: 'textPosition',
                type: 'VueSelect',
                label: 'Posição do texto',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'row',
                  },
                  {
                    label: 'Direita',
                    value: 'row-reverse',
                  },
                ],
              },
            ],
            toRemove: ['titleWeight'],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                type: 'List',
                name: 'buttons',
                maxItems: 2,
                itemLabel: 'Botão {index}',
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 15,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
          {
            name: 'background',
            label: 'Fundo',
            fields: [
              {
                name: 'backgroundType',
                type: 'VueSelect',
                label: 'Tipo de fundo',
                options: [
                  {
                    label: 'Cor sólida',
                    value: 'color',
                  },
                  {
                    label: 'Imagem',
                    value: 'image',
                  },
                ],
              },
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
                isEnabled({ backgroundType }) {
                  return backgroundType === 'color';
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Preencha a cor de fundo',
                  },
                },
              },
              {
                name: 'backgroundImage',
                type: 'ImageFile',
                label: 'Imagem de fundo',
                helpText:
                  'Largura recomendada: 1920px.<br>A altura depende do conteúdo e espaçamentos do módulo.',
                isEnabled({ backgroundType }) {
                  return backgroundType === 'image';
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Insira uma imagem de fundo',
                  },
                },
              },
              {
                name: 'differentMobileBackgroundImage',
                type: 'Checkbox',
                label: 'Usar imagem diferente no mobile',
                isEnabled({ backgroundType }) {
                  return backgroundType === 'image';
                },
              },
              {
                name: 'backgroundImageMobile',
                type: 'ImageFile',
                label: 'Imagem de fundo mobile',
                isEnabled({ backgroundType, differentMobileBackgroundImage }) {
                  return (
                    backgroundType === 'image' && differentMobileBackgroundImage
                  );
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Insira uma imagem de fundo para ser exibida em dispositivos móveis',
                  },
                },
              },
              {
                name: 'filterType',
                type: 'VueSelect',
                label: 'Tipo de filtro',
                options: [
                  {
                    label: 'Nenhum',
                    value: '',
                  },
                  {
                    label: 'Escuro',
                    value: 'dark',
                  },
                  {
                    label: 'Claro',
                    value: 'light',
                  },
                ],
                isEnabled({ backgroundType }) {
                  return backgroundType !== 'color';
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['backgroundColor'],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.highlight-title {
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  padding: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &--light {
    &::before {
      background: linear-gradient(
        148.09deg,
        rgba(255, 255, 255, 0.91) 21.66%,
        rgba(255, 255, 255, 0.72) 72.91%,
        rgba(255, 255, 255, 0) 98.53%
      );
      opacity: 0.85;
    }
  }

  &--dark {
    &::before {
      background: linear-gradient(
        148.09deg,
        rgba(0, 0, 0, 0.91) 21.66%,
        rgba(0, 0, 0, 0.72) 72.91%,
        rgba(0, 0, 0, 0) 98.53%
      );
      opacity: 0.75;
    }
  }

  &__container {
    position: relative;
    display: flex;
    justify-content: center;
    gap: 6.25%;
    width: 87.5%;
    margin: auto;
    flex-direction: var(--text-position);
  }

  .highlight-title__content {
    width: 100%;

    & > * + * {
      margin-top: 24px;
    }
  }

  &__buttons {
    ul {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }

    .button {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 48px;
      font-size: var(--content-size-desk, $size-normal);
      line-height: 1.2;
      min-width: 200px;
      border: 2px solid var(--button-color, $primary);
      background-color: transparent;

      &--featured {
        color: var(--button-text-color, $primary);
        background-color: var(--button-color, $primary);
        border: none;
      }
    }
  }
}

@include media-breakpoint-down(lg) {
  .highlight-title {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    &__container {
      flex-direction: column;
    }

    &__content {
      padding: 0;
    }

    &__buttons {
      ul {
        flex-direction: column;
      }

      .button {
        font-size: var(--content-size-mobile, $size-normal);
      }
    }
  }
}
</style>
